import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Translation of HubSpot enumeration values.
 *
 * HubSpot stores enum properties with a stable internal *value* (e.g. "customer",
 * "IN_PROGRESS", "Potenzieller Kunde") that is independent of the display language.
 * Those raw values contain spaces/pipes/umlauts, so this module maps each known
 * value to a translation key under `hubspot.*` (all four locale files) and the
 * per-locale labels live in the normal i18n pipeline. Unknown values fall back to
 * a humanized rendering of the raw value instead of leaking codes.
 */
export type HubspotEnumCategory = 'lifecycleStage' | 'leadStatus' | 'relationship';

// Lower-cased words kept lower-case when not the first token of a humanized label.
const SMALL_WORDS = new Set(['and', 'or', 'of', 'the', 'for', 'to', 'a', 'an', '&']);

/**
 * SCREAMING_SNAKE_CASE → "Title Case" fallback for unmapped values (the long tail
 * of HubSpot industries). Keeps the output readable instead of leaking raw codes.
 */
export function humanizeEnumValue(value: string): string {
  const cleaned = value.replace(/_/g, ' ').toLowerCase().trim();
  if (!cleaned) return value;
  return cleaned
    .split(/\s+/)
    .map((word, i) =>
      i > 0 && SMALL_WORDS.has(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
}

const LIFECYCLE_STAGE_KEYS: Record<string, string> = {
  subscriber: 'hubspot.lifecycleStage.subscriber',
  lead: 'hubspot.lifecycleStage.lead',
  marketingqualifiedlead: 'hubspot.lifecycleStage.marketingQualifiedLead',
  salesqualifiedlead: 'hubspot.lifecycleStage.salesQualifiedLead',
  opportunity: 'hubspot.lifecycleStage.opportunity',
  customer: 'hubspot.lifecycleStage.customer',
  // Custom stages keyed by their internal numeric id.
  '3403676913': 'hubspot.lifecycleStage.leadForLater',
  '5591079156': 'hubspot.lifecycleStage.partnerLead'
};

const LEAD_STATUS_KEYS: Record<string, string> = {
  NEW: 'hubspot.leadStatus.new',
  OPEN: 'hubspot.leadStatus.open',
  IN_PROGRESS: 'hubspot.leadStatus.inProgress',
  OPEN_DEAL: 'hubspot.leadStatus.openDeal',
  ATTEMPTED_TO_CONTACT: 'hubspot.leadStatus.attemptedToContact',
  CONNECTED: 'hubspot.leadStatus.connected',
  BAD_TIMING: 'hubspot.leadStatus.badTiming',
  MEETING: 'hubspot.leadStatus.meeting',
  QUALIFIED: 'hubspot.leadStatus.qualified',
  UNQUALIFIED: 'hubspot.leadStatus.unqualified',
  'Erste Email': 'hubspot.leadStatus.firstEmail',
  'Erste Email | 1. Follow-up': 'hubspot.leadStatus.firstEmailFollowUp1',
  'Erste Email | 2. Follow-up': 'hubspot.leadStatus.firstEmailFollowUp2',
  'Termin vereinbart': 'hubspot.leadStatus.appointmentScheduled',
  'Nicht passend': 'hubspot.leadStatus.notSuitable',
  'Keine Konkretisierung': 'hubspot.leadStatus.noConcretization'
};

/**
 * Keyed by the raw HubSpot "kontaktbeziehung" (contact relationship) value,
 * which historically uses German internal strings. Deliberately NOT named after
 * the licence: the labels read as licence names because that is how sales
 * describes a relationship, but the property is unrelated to
 * `CreatePartnerLeadRequest.licenseType` and shares none of its values — adding
 * that enum's values here would silently do nothing.
 */
const RELATIONSHIP_KEYS: Record<string, string> = {
  'Potenzieller Kunde': 'hubspot.relationship.b2bLicense',
  'Potenzieller Vertriebspartner': 'hubspot.relationship.partnerLicenseWithoutSeats',
  'Potenzieller B2B Partner': 'hubspot.relationship.partnerLicenseWithSeats',
  'Internationale Partner-Lizenz (Mit Seat)':
    'hubspot.relationship.internationalPartnerLicenseWithSeat',
  'Internationale Partner-Lizenz (Ohne Seat)':
    'hubspot.relationship.internationalPartnerLicenseWithoutSeat',
  'Internationale B2B-Lizenz (Enduser)': 'hubspot.relationship.internationalB2bLicenseEndUser',
  'Externe Workforce zur Produktentwicklung':
    'hubspot.relationship.externalWorkforceProductDevelopment',
  'Sonstiger Multiplikator': 'hubspot.relationship.communityCatalyst',
  'Technischer Integrationspartner': 'hubspot.relationship.technicalIntegrationPartner'
};

const KEY_TABLES: Record<HubspotEnumCategory, Record<string, string>> = {
  lifecycleStage: LIFECYCLE_STAGE_KEYS,
  leadStatus: LEAD_STATUS_KEYS,
  relationship: RELATIONSHIP_KEYS
};

/**
 * Returns a translator for HubSpot enum values. The returned function is stable
 * for a given language, so it is safe to list in `useMemo`/`useCallback` deps.
 *
 * Pass a non-empty raw value; empty/null inputs return '' so callers keep their
 * own placeholder (e.g. `value ? label(cat, value) : '—'`).
 */
export function useHubspotLabel() {
  const { t } = useTranslation();

  return useCallback(
    (category: HubspotEnumCategory, value: string | null | undefined): string => {
      if (!value) return '';
      const key = KEY_TABLES[category][value];
      return key ? t(key) : humanizeEnumValue(value);
    },
    [t]
  );
}
